<template>
  <div class="content-wrapper ataviti-home">
    <div class="content-body">

      <!-- HERO -->
      <header class="ataviti-home__banner">
        <div class="ataviti-home__banner-text">
          <p class="ataviti-home__banner-eyebrow">
            Chantiers qualité
          </p>
          <h1 class="ataviti-home__title">
            Que souhaitez-vous faire&nbsp;?
          </h1>
          <p class="ataviti-home__banner-sub">
            Pilotez vos chantiers correctifs, projets structurants, réunions irritant
            et suivis de processus dans un espace unique.
          </p>
        </div>
      </header>

      <!-- Filtre projet actif (arrivée depuis un projet, ou sélection dans le filtre « Projet ») -->
      <b-alert
        v-if="filtreProjet"
        show
        variant="primary"
        class="mt-1 mb-0 px-1 py-50"
      >
        <div class="d-flex align-items-center justify-content-between">
          <span>
            <feather-icon
              icon="LayersIcon"
              size="14"
              class="mr-50"
            />
            Chantiers du projet : <strong>{{ (projetSelectionne && projetSelectionne.nom) || '…' }}</strong>
          </span>
          <b-button
            variant="flat-primary"
            size="sm"
            @click="clearProjetFilter"
          >
            <feather-icon
              icon="XIcon"
              size="14"
              class="mr-25"
            />
            Retirer le filtre
          </b-button>
        </div>
        <p
          v-if="projetSelectionne && projetSelectionne.description"
          class="mb-0 mt-50"
          style="white-space: pre-line;"
        >
          {{ projetSelectionne.description }}
        </p>
      </b-alert>

      <!-- TOOLBAR -->
      <div class="chantiers-toolbar">
        <div class="chantiers-toolbar__count">
          <feather-icon
            icon="LayersIcon"
            size="16"
            class="mr-50"
          />
          <strong>{{ totalCount }}</strong>
          <span class="ml-50">
            chantier{{ totalCount > 1 ? 's' : '' }}
          </span>
        </div>

        <div class="chantiers-toolbar__actions">
          <b-button
            v-if="$can('CHANTIER:read')"
            variant="outline-primary"
            :to="{ name: 'chantiers-recap-public' }"
          >
            <feather-icon
              icon="ColumnsIcon"
              size="14"
              class="mr-50"
            />
            Récap
          </b-button>
          <b-button
            v-if="$can('CHANTIER:read')"
            variant="outline-primary"
            :to="{ name: 'chantiers-bulletin' }"
          >
            <feather-icon
              icon="FileTextIcon"
              size="14"
              class="mr-50"
            />
            Bulletin
          </b-button>
          <b-button
            v-if="$can('CHANTIER:create')"
            variant="primary"
            @click="goToCreate"
          >
            <feather-icon
              icon="PlusIcon"
              size="14"
              class="mr-50"
            />
            Nouveau chantier
          </b-button>
        </div>
      </div>

      <!-- SEARCH + FILTERS -->
      <div class="chantiers-filters">
        <b-form-group
          label-for="chantier-search"
          class="mb-2"
        >
          <b-input-group>
            <b-input-group-prepend is-text>
              <feather-icon
                icon="SearchIcon"
                size="14"
              />
            </b-input-group-prepend>
            <b-form-input
              id="chantier-search"
              v-model="search"
              placeholder="Rechercher un chantier (nom, description, sous-catégorie…)"
              debounce="200"
            />
            <b-input-group-append
              v-if="search"
              is-text
              class="chantiers-filters__clear"
            >
              <feather-icon
                icon="XIcon"
                size="14"
                @click="search = ''"
              />
            </b-input-group-append>
          </b-input-group>
        </b-form-group>

        <div class="row">
          <div class="col-12 col-md-4">
            <b-form-group
              label="Catégorie"
              label-for="filter-cat"
            >
              <b-form-select
                id="filter-cat"
                v-model="filtreCategorie"
                :options="categorieOptions"
              />
            </b-form-group>
          </div>
          <div class="col-12 col-md-4">
            <b-form-group
              label="Origine"
              label-for="filter-origine"
            >
              <b-form-select
                id="filter-origine"
                v-model="filtreOrigine"
                :options="origineOptions"
              />
            </b-form-group>
          </div>
          <div class="col-12 col-md-4">
            <b-form-group
              label="Sous-catégorie"
              label-for="filter-subcat"
            >
              <b-form-select
                id="filter-subcat"
                v-model="filtreSousCategorie"
                :options="sousCategorieOptions"
              />
            </b-form-group>
          </div>
          <div class="col-12 col-md-4">
            <b-form-group
              label="Statut"
              label-for="filter-statut"
            >
              <b-form-select
                id="filter-statut"
                v-model="filtreStatut"
                :options="statutOptions"
              />
            </b-form-group>
          </div>
          <div class="col-12 col-md-4">
            <b-form-group
              label="Priorité"
              label-for="filter-priorite"
            >
              <b-form-select
                id="filter-priorite"
                v-model="filtrePriorite"
                :options="prioriteOptions"
              />
            </b-form-group>
          </div>
          <div class="col-12 col-md-4">
            <b-form-group
              label="Visibilité"
              label-for="filter-vis"
            >
              <b-form-select
                id="filter-vis"
                v-model="filtreVisibilite"
                :options="visibiliteOptions"
              />
            </b-form-group>
          </div>
          <div class="col-12 col-md-4">
            <b-form-group
              label="Processus"
              label-for="filter-processus"
            >
              <b-form-select
                id="filter-processus"
                v-model="filtreProcessus"
                :options="processusOptions"
              />
            </b-form-group>
          </div>
          <div class="col-12 col-md-4">
            <b-form-group
              label="Projet"
              label-for="filter-projet"
            >
              <b-form-select
                id="filter-projet"
                v-model="filtreProjet"
                :options="projetOptions"
              />
            </b-form-group>
          </div>
        </div>

        <div
          v-if="hasActiveFilters"
          class="chantiers-filters__actions"
        >
          <b-button
            variant="flat-secondary"
            size="sm"
            @click="resetFilters"
          >
            <feather-icon
              icon="XCircleIcon"
              size="14"
              class="mr-50"
            />
            Réinitialiser les filtres
          </b-button>
        </div>
      </div>

      <!-- TRI (cartes) + BASCULEUR DE VUE (cartes / tableau) -->
      <div class="chantiers-viewbar">
        <div
          v-if="viewMode === 'cards'"
          class="chantiers-sortbar"
        >
          <span class="chantiers-sortbar__label">Trier par</span>
          <b-form-select
            v-model="sortBy"
            :options="sortOptions"
            size="sm"
            @change="reloadFromFirstPage"
          />
          <b-button
            variant="outline-primary"
            size="sm"
            :title="sortDesc ? 'Décroissant' : 'Croissant'"
            @click="toggleSortDir"
          >
            <feather-icon
              :icon="sortDesc ? 'ArrowDownIcon' : 'ArrowUpIcon'"
              size="14"
            />
          </b-button>
        </div>
        <b-button-group
          size="sm"
          class="chantiers-viewbar__toggle"
        >
          <b-button
            :variant="viewMode === 'cards' ? 'primary' : 'outline-primary'"
            @click="viewMode = 'cards'"
          >
            <feather-icon
              icon="GridIcon"
              size="14"
            />
          </b-button>
          <b-button
            :variant="viewMode === 'table' ? 'primary' : 'outline-primary'"
            @click="viewMode = 'table'"
          >
            <feather-icon
              icon="ListIcon"
              size="14"
            />
          </b-button>
        </b-button-group>
      </div>

      <!-- LOADING -->
      <div
        v-if="loading"
        class="chantiers-empty"
      >
        <b-spinner class="mb-1" />
        <p class="mt-1 mb-0">
          Chargement des chantiers…
        </p>
      </div>

      <!-- ERROR -->
      <div
        v-else-if="error"
        class="chantiers-empty chantiers-empty--error"
      >
        <feather-icon
          icon="AlertTriangleIcon"
          size="32"
        />
        <p class="mt-1 mb-1">
          {{ error }}
        </p>
        <b-button
          variant="outline-primary"
          size="sm"
          @click="loadChantiers"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="14"
            class="mr-50"
          />
          Réessayer
        </b-button>
      </div>

      <!-- EMPTY -->
      <div
        v-else-if="chantiers.length === 0"
        class="chantiers-empty"
      >
        <feather-icon
          icon="SearchIcon"
          size="32"
        />
        <p class="mt-1 mb-0">
          Aucun chantier ne correspond à ces critères.
        </p>
      </div>

      <!-- LIST (cartes) -->
      <div
        v-else-if="viewMode === 'cards'"
        class="row"
      >
        <div
          v-for="c in chantiers"
          :key="c.id"
          class="col-12 col-md-6 col-lg-4 d-flex"
        >
          <article
            class="chantier-card"
            @click="goToDetail(c)"
          >
            <div class="chantier-card__header">
              <span
                class="badge chantier-card__cat-badge"
                :style="categoryBadgeStyle(c.categorieCouleur)"
              >{{ c.categorieLibelle }}</span>

              <span
                v-if="c.visibilite === 1"
                class="badge badge-success chantier-card__vis"
              >
                <feather-icon
                  icon="GlobeIcon"
                  size="12"
                  class="mr-25"
                />
                Public
              </span>
              <span
                v-else
                class="badge badge-light chantier-card__vis chantier-card__vis--prive"
              >
                <feather-icon
                  icon="LockIcon"
                  size="12"
                  class="mr-25"
                />
                Privé
              </span>
            </div>

            <h3 class="chantier-card__title">
              {{ c.nom }}
            </h3>
            <p class="chantier-card__sub">
              {{ c.sousCategorie || c.origineLibelle }}
            </p>

            <div class="chantier-card__badges">
              <span
                class="badge chantier-card__statut"
                :style="statutBadgeStyle(c.statut)"
              >{{ statutLabel(c.statut) }}</span>
              <span
                v-if="c.priorite != null && prioriteLabel(c.priorite)"
                class="badge chantier-card__statut"
                :style="prioriteBadgeStyle(c.priorite)"
              >{{ prioriteLabel(c.priorite) }}</span>
              <span
                v-if="c.dateEcheance"
                class="chantier-card__echeance"
              >
                <feather-icon
                  icon="CalendarIcon"
                  size="12"
                  class="mr-25"
                />
                {{ formatDate(c.dateEcheance) }}
              </span>
            </div>

            <div class="chantier-card__footer">
              <span class="chantier-card__metric">
                <feather-icon
                  icon="TargetIcon"
                  size="14"
                  class="mr-50"
                />
                {{ c.nbPlansAction }} plan(s)
              </span>
              <span class="chantier-card__metric">
                <feather-icon
                  icon="UsersIcon"
                  size="14"
                  class="mr-50"
                />
                {{ c.nbParticipants }} participant(s)
              </span>
              <span class="chantier-card__cta">
                Ouvrir
                <feather-icon
                  icon="ArrowRightIcon"
                  size="14"
                  class="ml-25"
                />
              </span>
            </div>
          </article>
        </div>
      </div>

      <!-- LIST (tableau) -->
      <b-table
        v-else
        :items="chantiers"
        :fields="tableColumns"
        responsive
        primary-key="id"
        hover
        striped
        class="chantiers-table"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        :no-local-sorting="true"
        no-sort-reset
        @sort-changed="onSortChanged"
        @row-clicked="goToDetail"
      >
        <template #cell(nom)="d">
          <span class="chantiers-table__nom">{{ d.item.nom }}</span>
        </template>
        <template #cell(statut)="d">
          <span
            class="badge"
            :style="statutBadgeStyle(d.item.statut)"
          >{{ statutLabel(d.item.statut) }}</span>
        </template>
        <template #cell(priorite)="d">
          <span
            v-if="d.item.priorite != null && prioriteLabel(d.item.priorite)"
            class="badge"
            :style="prioriteBadgeStyle(d.item.priorite)"
          >{{ prioriteLabel(d.item.priorite) }}</span>
          <span v-else>—</span>
        </template>
        <template #cell(dateEcheance)="d">
          {{ d.item.dateEcheance ? formatDate(d.item.dateEcheance) : '—' }}
        </template>
        <template #cell(created)="d">
          {{ d.item.created ? formatDate(d.item.created) : '—' }}
        </template>
        <template #cell(visibilite)="d">
          {{ d.item.visibilite === 1 ? 'Public' : 'Privé' }}
        </template>
      </b-table>

      <!-- PAGINATION -->
      <div
        v-if="!loading && !error && totalCount > 0"
        class="chantiers-pagination"
      >
        <span class="text-muted">
          Affichage de {{ paginationFrom }} à {{ paginationTo }} sur {{ totalCount }} chantier{{ totalCount > 1 ? 's' : '' }}
        </span>
        <b-pagination
          v-model="pageNumber"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
          class="mb-0"
          prev-class="prev-item"
          next-class="next-item"
        >
          <template #prev-text>
            <feather-icon
              icon="ChevronLeftIcon"
              size="18"
            />
          </template>
          <template #next-text>
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
            />
          </template>
        </b-pagination>
      </div>

      <!-- CREATE MODAL (composant partagé liste / détail) -->
      <chantier-create-modal
        ref="createModal"
        @created="onChantierCreated"
      />

    </div>
  </div>
</template>

<script>
import {
  BAlert,
  BButton,
  BButtonGroup,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BInputGroup,
  BInputGroupAppend,
  BInputGroupPrepend,
  BPagination,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import ChantierCreateModal from '@/components/chantiers/ChantierCreateModal.vue'

export default {
  name: 'ChantiersListView',
  components: {
    BAlert,
    BButton,
    BButtonGroup,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BInputGroup,
    BInputGroupAppend,
    BInputGroupPrepend,
    BPagination,
    BSpinner,
    BTable,
    ChantierCreateModal,
  },
  data() {
    return {
      chantiers: [],
      totalCount: 0,
      pageNumber: 1,
      pageSize: 9,
      loading: true,
      error: null,
      search: '',
      filtreCategorie: null,
      filtreOrigine: null,
      filtreSousCategorie: null,
      filtreStatut: null,
      filtrePriorite: null,
      filtreVisibilite: null,
      filtreProcessus: null,
      // Filtre par projet d'amélioration continue : initialisé depuis la query (clic depuis un projet).
      // Init direct dans data() → pas de double chargement au montage (le watcher ne se déclenche
      // que sur un changement ultérieur, ex. retrait du filtre).
      filtreProjet: (this.$route && this.$route.query && this.$route.query.projetId) || null,
      // Projets d'amélioration continue (source du sélecteur « Projet » + description affichée).
      projets: [],
      viewMode: localStorage.getItem('myataviti:chantiers:viewMode') || 'cards',
      // Tri serveur partagé par les 2 vues (cartes + tableau). Défaut : création la plus récente d'abord.
      sortBy: 'created',
      sortDesc: true,
      tableColumns: [
        { key: 'nom', label: 'Nom', sortable: true },
        { key: 'categorieLibelle', label: 'Catégorie', sortable: true },
        { key: 'statut', label: 'Statut', sortable: true },
        { key: 'priorite', label: 'Priorité', sortable: true },
        { key: 'dateEcheance', label: 'Échéance', sortable: true },
        { key: 'created', label: 'Créé le', sortable: true },
        { key: 'nbPlansAction', label: 'Plans', sortable: true },
        { key: 'nbParticipants', label: 'Participants', sortable: true },
        { key: 'visibilite', label: 'Visibilité', sortable: true },
      ],
      categories: [],
      origines: [],
      sousCategories: [],
      statuts: [],
      priorites: [],
      processus: [],
      debounceTimeout: null,
    }
  },
  computed: {
    categorieOptions() {
      return [
        { value: null, text: 'Toutes les catégories' },
        ...this.categories.map(c => ({ value: c.id, text: c.libelle })),
      ]
    },
    origineOptions() {
      return [
        { value: null, text: 'Toutes les origines' },
        ...this.origines.map(o => ({ value: o.id, text: o.libelle })),
      ]
    },
    sousCategorieOptions() {
      return [
        { value: null, text: 'Toutes les sous-catégories' },
        ...this.sousCategories.map(s => ({ value: s, text: s })),
      ]
    },
    statutOptions() {
      return [
        { value: null, text: 'Tous les statuts' },
        ...this.statuts.map(s => ({ value: s.id, text: s.valeur })),
      ]
    },
    prioriteOptions() {
      return [
        { value: null, text: 'Toutes les priorités' },
        ...this.priorites.map(p => ({ value: p.id, text: p.valeur })),
      ]
    },
    visibiliteOptions() {
      return [
        { value: null, text: 'Public et privé' },
        { value: 1, text: 'Public uniquement' },
        { value: 0, text: 'Privé uniquement' },
      ]
    },
    processusOptions() {
      return [
        { value: null, text: 'Tous les processus' },
        ...this.processus.map(p => ({ value: p.id, text: p.libelle })),
      ]
    },
    projetOptions() {
      return [
        { value: null, text: 'Tous les projets' },
        ...this.projets.map(p => ({ value: p.id, text: p.nom })),
      ]
    },
    // Projet actuellement filtré (pour afficher nom + description dans le bandeau, réactif).
    projetSelectionne() {
      return this.projets.find(p => p.id === this.filtreProjet) || null
    },
    hasActiveFilters() {
      return !!(
        this.search
        || this.filtreCategorie
        || this.filtreOrigine
        || this.filtreSousCategorie
        || this.filtreStatut !== null
        || this.filtrePriorite !== null
        || this.filtreVisibilite !== null
        || this.filtreProcessus
        || this.filtreProjet
      )
    },
    sortOptions() {
      // Source unique pour le menu « Trier par » des cartes : les colonnes triables du tableau.
      return this.tableColumns
        .filter(c => c.sortable)
        .map(c => ({ value: c.key, text: c.label }))
    },
    paginationFrom() {
      return this.pageSize * (this.pageNumber - 1) + (this.chantiers.length ? 1 : 0)
    },
    paginationTo() {
      return this.pageSize * (this.pageNumber - 1) + this.chantiers.length
    },
  },
  watch: {
    search() { this.debouncedReload() },
    filtreCategorie() { this.reloadFromFirstPage() },
    filtreOrigine() { this.reloadFromFirstPage() },
    filtreSousCategorie() { this.reloadFromFirstPage() },
    filtreStatut() { this.reloadFromFirstPage() },
    filtrePriorite() { this.reloadFromFirstPage() },
    filtreVisibilite() { this.reloadFromFirstPage() },
    filtreProcessus() { this.reloadFromFirstPage() },
    filtreProjet(val) {
      this.syncProjetQuery(val)
      this.reloadFromFirstPage()
    },
    viewMode(v) { localStorage.setItem('myataviti:chantiers:viewMode', v) },
    pageNumber() { this.loadChantiers() },
  },
  async created() {
    await this.loadFilterSources()
    await this.loadChantiers()
  },
  methods: {
    async loadFilterSources() {
      try {
        const [categories, origines, statuts, priorites, sousCategories, processus, projets] = await Promise.all([
          this.$http.myressif.chantiers.listCategories(),
          this.$http.myressif.chantiers.listOrigines(),
          this.$http.myressif.chantiers.getStatuts(),
          this.$http.myressif.chantiers.getPriorites(),
          this.$http.myressif.chantiers.getSousCategories(),
          this.$http.myressif.chantiers.listProcessus(),
          this.$http.myressif.chantierProjets.listRef(),
        ])
        this.categories = (categories && categories.items) || []
        this.origines = (origines && origines.items) || []
        this.statuts = statuts || []
        this.priorites = priorites || []
        this.sousCategories = sousCategories || []
        this.processus = (processus && processus.items) || []
        this.projets = projets || []
      } catch (e) {
        // Filter sources are non-blocking; the list can still load.
        this.categories = []
        this.origines = []
        this.statuts = []
        this.priorites = []
        this.sousCategories = []
        this.processus = []
        this.projets = []
      }
    },
    async loadChantiers() {
      this.loading = true
      this.error = null
      try {
        const params = {
          pageNumber: this.pageNumber,
          pageSize: this.pageSize,
          categorieId: this.filtreCategorie || undefined,
          origineId: this.filtreOrigine || undefined,
          sousCategorie: this.filtreSousCategorie || undefined,
          statut: this.filtreStatut !== null ? this.filtreStatut : undefined,
          priorite: this.filtrePriorite !== null ? this.filtrePriorite : undefined,
          visibilite: this.filtreVisibilite !== null ? this.filtreVisibilite : undefined,
          processusId: this.filtreProcessus || undefined,
          projetId: this.filtreProjet || undefined,
          search: this.search ? this.search.trim() : undefined,
          // L'API trie le DTO ChantierListItemDto : le nom de propriété doit être en PascalCase
          // (created -> Created, dateEcheance -> DateEcheance, …). On capitalise la clé de colonne.
          orderBy: this.sortBy
            ? this.sortBy.charAt(0).toUpperCase() + this.sortBy.slice(1)
            : undefined,
          orderByDescending: this.sortDesc,
        }
        const response = await this.$http.myressif.chantiers.search(params)
        this.chantiers = (response && response.items) || []
        this.totalCount = (response && response.totalCount) || 0
      } catch (e) {
        this.chantiers = []
        this.totalCount = 0
        this.error = 'Une erreur est survenue lors du chargement des chantiers.'
      } finally {
        this.loading = false
      }
    },
    reloadFromFirstPage() {
      if (this.pageNumber !== 1) {
        // The pageNumber watcher will trigger the reload.
        this.pageNumber = 1
      } else {
        this.loadChantiers()
      }
    },
    debouncedReload() {
      if (this.debounceTimeout) clearTimeout(this.debounceTimeout)
      this.debounceTimeout = setTimeout(() => {
        this.reloadFromFirstPage()
      }, 300)
    },
    resetFilters() {
      this.search = ''
      this.filtreCategorie = null
      this.filtreOrigine = null
      this.filtreSousCategorie = null
      this.filtreStatut = null
      this.filtrePriorite = null
      this.filtreVisibilite = null
      this.filtreProcessus = null
      this.clearProjetFilter()
    },
    // Garde l'URL en phase avec le filtre projet (pose/retire ?projetId sans recharger la route).
    syncProjetQuery(val) {
      if ((this.$route.query.projetId || null) === (val || null)) return
      const query = { ...this.$route.query }
      if (val) query.projetId = val
      else delete query.projetId
      this.$router.replace({ query }).catch(() => {})
    },
    // Retire le filtre projet (le watcher filtreProjet nettoie la query et recharge).
    clearProjetFilter() {
      this.filtreProjet = null
    },
    onSortChanged(ctx) {
      // Tri natif b-table (vue tableau) : on relit le tri depuis l'évènement puis on recharge côté serveur.
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.reloadFromFirstPage()
    },
    toggleSortDir() {
      this.sortDesc = !this.sortDesc
      this.reloadFromFirstPage()
    },
    goToDetail(c) {
      this.$router.push({ name: 'chantier-detail', params: { id: c.id } })
    },
    async goToCreate() {
      this.$refs.createModal.open()
    },
    onChantierCreated(newId) {
      this.$router.push({ name: 'chantier-detail', params: { id: newId } })
    },
    prioriteLabel(prioriteId) {
      const p = this.priorites.find(pr => pr.id === prioriteId)
      return p ? p.valeur : ''
    },
    prioriteBadgeStyle(prioriteId) {
      const p = this.priorites.find(pr => pr.id === prioriteId)
      const couleur = p ? p.couleur : '#5A7373'
      return { backgroundColor: couleur, color: '#fff' }
    },
    statutLabel(statutId) {
      const s = this.statuts.find(st => st.id === statutId)
      return s ? s.valeur : 'Inconnu'
    },
    statutBadgeStyle(statutId) {
      const s = this.statuts.find(st => st.id === statutId)
      const couleur = s ? s.couleur : '#5A7373'
      return { backgroundColor: couleur, color: '#fff' }
    },
    categoryBadgeStyle(couleur) {
      const c = couleur || '#5A7373'
      return { backgroundColor: c, color: '#fff' }
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleDateString('fr-FR')
    },
  },
}
</script>

<style scoped lang="scss">
$at-green:   #1c4d4d;
$at-green-7: #143838;
$at-yellow:  #fcf5a6;
$at-orange:  #ff6131;
$at-beige:   #fffced;
$at-ink-900: #0F1F1F;
$at-ink-700: #2A3F3F;
$at-ink-500: #5A7373;
$at-ink-300: #A6B8B8;
$at-ink-100: #E4ECEC;
$at-ink-50:  #F2F6F6;

.ataviti-home {
  &__banner {
    background: $at-yellow;
    border-radius: 12px;
    padding: 28px 32px;
    margin-bottom: 24px;
    position: relative;
    overflow: hidden;
  }
  &__banner-eyebrow {
    color: $at-green;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: .08em;
    text-transform: uppercase;
    margin: 0 0 8px;
  }
  &__banner-sub {
    color: $at-ink-700;
    font-size: 14px;
    margin: 10px 0 0;
  }
  &__title {
    font-family: 'Iowan Old Style', 'Palatino Linotype', Georgia, serif;
    font-size: 30px;
    color: $at-green;
    font-weight: 500;
    margin: 0;
    letter-spacing: -.01em;
    line-height: 1.15;
  }
}

.chantiers-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px;
  background: #fff;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 16px;

  &__actions {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }
  &__count {
    display: inline-flex;
    align-items: center;
    color: $at-ink-700;
    font-size: 14px;
    strong { color: $at-green; font-size: 16px; }
  }
}

.chantiers-filters {
  background: #fff;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 24px;

  &__clear {
    cursor: pointer;
    .feather { color: $at-ink-500; }
    &:hover .feather { color: $at-orange; }
  }
  &__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 4px;
  }
}

.chantiers-empty {
  text-align: center;
  padding: 48px 16px;
  color: $at-ink-500;
  background: #fff;
  border: 1px dashed $at-ink-100;
  border-radius: 10px;

  &--error {
    color: $at-orange;
    border-color: rgba(255, 97, 49, .3);
  }
}

.chantiers-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
  padding: 12px 4px;
}

.chantiers-viewbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;

  &__toggle { margin-left: auto; }
}

.chantiers-sortbar {
  display: flex;
  align-items: center;
  gap: 8px;

  &__label {
    color: $at-ink-500;
    font-size: 13px;
    white-space: nowrap;
  }

  .custom-select { min-width: 180px; }
}

.chantiers-table {
  background: #fff;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  overflow: hidden;

  ::v-deep tbody tr { cursor: pointer; }

  &__nom { font-weight: 600; color: $at-ink-900; }

  .badge {
    font-size: 11px;
    padding: 5px 10px;
    border-radius: 999px;
  }
}

.chantier-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #fff;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  padding: 22px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;

  &:hover {
    border-color: $at-green;
    box-shadow: 0 4px 16px rgba(28, 77, 77, .08);
    transform: translateY(-1px);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    flex-wrap: wrap;
    gap: 6px;
  }
  &__cat-badge {
    font-size: 11px;
    font-weight: 600;
    padding: 5px 10px;
    border-radius: 999px;
    letter-spacing: .02em;
  }
  &__vis {
    font-size: 11px;
    padding: 5px 10px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;

    &--prive {
      background: $at-ink-100;
      color: $at-ink-700;
    }
  }
  &__title {
    font-size: 16px;
    color: $at-ink-900;
    font-weight: 600;
    margin: 0 0 4px;
    line-height: 1.3;
  }
  &__sub {
    color: $at-orange;
    font-size: 13px;
    font-weight: 500;
    margin: 0 0 12px;
  }
  &__badges {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin: 0 0 16px;
  }
  &__statut {
    font-size: 11px;
    padding: 5px 10px;
    border-radius: 999px;
  }
  &__echeance {
    display: inline-flex;
    align-items: center;
    color: $at-ink-500;
    font-size: 12px;
  }
  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding-top: 12px;
    border-top: 1px solid $at-ink-100;
  }
  &__metric {
    display: inline-flex;
    align-items: center;
    color: $at-ink-500;
    font-size: 12px;
  }
  &__cta {
    display: inline-flex;
    align-items: center;
    color: $at-green;
    font-size: 13px;
    font-weight: 600;
  }
}

.cat-iso       { background: rgba(28, 77, 77, .10);  color: $at-green;  }
.cat-lucie     { background: rgba(47, 125, 107, .12); color: #2f7d6b;   }
.cat-ecovadis  { background: rgba(240, 168, 48, .15); color: #b07415;   }
.cat-projet    { background: rgba(255, 97, 49, .12);  color: $at-orange; }
.cat-irritant  { background: rgba(216, 65, 42, .12);  color: #d8412a;   }
.cat-suivi     { background: rgba(90, 115, 115, .12); color: $at-ink-700; }

@media (max-width: 768px) {
  .chantier-card { margin-bottom: 16px; }
}
</style>
